import * as _ from 'lodash-es';
import { bubbleFormats } from 'quill/blots/block';
import Quill, { Parchment } from 'quill/core';
import type { Bounds, EmitterSource, Range } from 'quill/core';
import { useEffect, useRef, useState } from 'react';
import type QuillEditor from './QuillEditor';
import type { Line } from './types';
import getClosestLine from './utils/getClosestLine';

const Module = Quill.import('core/module');

const EVENTS = {
  CHANGE: 'prefix-listener-change',
  RESET: 'prefix-listener-reset',
};

const allowedPrecedingCharRegex = /[\s{[("<]/;
export function isPrecedingCharAllowed(char: string | undefined) {
  return char ? allowedPrecedingCharRegex.test(char) : true;
}

const checkBlotsInSameLine = (
  blot1: Parchment.LeafBlot | null,
  blot2: Parchment.LeafBlot | null,
) => {
  const line = getClosestLine(blot1);
  return line ? line === getClosestLine(blot2) : false;
};

function getLeafAndPrefixText(
  quill: Quill,
  rangeIndex: number,
): [Parchment.TextBlot, string] | [null, ''] {
  const [leaf, offset] = quill.getLeaf(rangeIndex);
  if (leaf instanceof Parchment.TextBlot) {
    return [leaf, leaf.value().slice(0, offset)];
  }

  return [null, ''];
}

type Setting = { prefix: string; isAllowedInTitle: boolean };
type State = {
  prefix: string;

  /**
   * The text after the prefix. This does not include the prefix.
   */
  text: string;
  blot: Line;
  clientRects: [Bounds, ...Bounds[]];
  isInTitle: boolean;

  /**
   * The range of the prefix. This does not include the text after the prefix.
   */
  prefixRange: Range;
};

const quillToSettings = new WeakMap<Quill, Setting[]>();

type PrefixListenerOptions = {
  persistOnSelectionChange?: boolean;
};

class PrefixListener extends Module<PrefixListenerOptions> {
  declare quill: QuillEditor;

  private activeState: State | null = null;

  constructor(quill: QuillEditor, options: PrefixListenerOptions) {
    super(quill, options);

    this.setupListener();
  }

  private get settings() {
    return quillToSettings.get(this.quill) || [];
  }

  private getClientRects(
    index: number,
    length: number,
  ): [Bounds, ...Bounds[]] | null {
    const rects = this.quill.getClientRects(index, length);
    const firstRect = rects[0];
    if (firstRect) {
      return [firstRect, ...rects.slice(1)];
    }
    const bounds = this.quill.getBounds(index, length);
    return bounds ? [bounds] : null;
  }

  private setActiveState(state: State | null) {
    if (this.activeState !== state) {
      this.activeState = state;
      this.quill.emitter.emit(EVENTS.CHANGE, state);
      if (this.activeState) {
        this.quill.root.setAttribute('autocorrect', 'off');
      } else {
        this.quill.root.removeAttribute('autocorrect');
      }
    }
  }

  private getSetting(options: {
    range: Range;
    leaf: Parchment.LeafBlot | null;
    prefixText: string;
    source: EmitterSource;
  }) {
    if (options.source !== Quill.sources.USER) return null;

    const setting = this.settings.find(({ prefix }) =>
      options.prefixText.endsWith(prefix),
    );

    if (!setting) return null;

    const precedingChar = (() => {
      if (options.prefixText.length > setting.prefix.length) {
        return options.prefixText.at(-1 - setting.prefix.length);
      }

      const [precedingLeaf, prefixText] = getLeafAndPrefixText(
        this.quill,
        options.range.index - setting.prefix.length,
      );
      const lastCharInPrecedingLeaf = prefixText.at(-1);

      return lastCharInPrecedingLeaf &&
        checkBlotsInSameLine(options.leaf, precedingLeaf)
        ? lastCharInPrecedingLeaf
        : '';
    })();

    return isPrecedingCharAllowed(precedingChar) &&
      !bubbleFormats(options.leaf).code
      ? setting
      : null;
  }

  private setupListener() {
    const { quill } = this;

    quill.on(Quill.events.TEXT_CHANGE, (delta, _old, source) => {
      if (source === Quill.sources.SILENT) return;

      // With queueMicrotask, we ensure this runs after Quill has restored the selection
      queueMicrotask(() => {
        quill.update(Quill.sources.SILENT); // Make sure to get range in consistent state
        const [range] = quill.selection.getRange();
        if (!range) {
          this.setActiveState(null);
          return;
        }

        const [leaf, prefixText] = getLeafAndPrefixText(quill, range.index);
        const setting = this.getSetting({
          range,
          leaf,
          prefixText,
          source,
        });

        if (setting) {
          const [line] = quill.getLine(range.index);
          const isInTitle = quill.titleLine === line;
          const hasMatch = line && (!isInTitle || setting.isAllowedInTitle);

          if (hasMatch) {
            const { prefix } = setting;
            const clientRects = this.getClientRects(
              range.index - prefix.length,
              prefix.length,
            );
            if (clientRects) {
              this.setActiveState({
                blot: line,
                clientRects,
                prefix,
                text: '',
                isInTitle,
                prefixRange: {
                  index: range.index - prefix.length,
                  length: prefix.length,
                },
              });

              return;
            }
          }
        }

        if (this.activeState) {
          const lastIndex = prefixText.lastIndexOf(this.activeState.prefix);
          if (lastIndex === -1) {
            this.setActiveState(null);
          } else {
            const newRange = {
              index: delta.transform(this.activeState.prefixRange.index),
              length: this.activeState.prefixRange.length,
            };
            const distance = range.index - newRange.index;
            const canReusePrefixRange =
              distance > 0 && prefixText.length >= distance;

            const index = canReusePrefixRange
              ? prefixText.length - distance
              : lastIndex;

            const text = prefixText.slice(
              index + this.activeState.prefix.length,
            );

            this.setActiveState({
              ...this.activeState,
              text,
              prefixRange: newRange,
              clientRects:
                this.getClientRects(
                  newRange.index,
                  newRange.length + text.length,
                ) ?? this.activeState.clientRects,
            });
          }
        }
      });
    });

    if (!this.options.persistOnSelectionChange) {
      quill.on(Quill.events.SELECTION_CHANGE, (_range, _old, source) => {
        if (source === Quill.sources.USER) {
          this.setActiveState(null);
        }
      });
    }

    quill.on(EVENTS.RESET, () => {
      this.setActiveState(null);
    });
  }
}

type UsePrefixListenerOptions = (Setting | string)[];

const EMPTY_STATE = {
  clientRects: undefined,
  text: undefined,
  prefix: undefined,
  reset: undefined,
  prefixRange: undefined,
  blot: undefined,
};

export const usePrefixListener = (
  quill: Quill | null,
  options: UsePrefixListenerOptions,
) => {
  const settingsRef = useRef<Setting[]>([]);
  const [state, setState] = useState<
    (State & { reset: () => void }) | typeof EMPTY_STATE
  >(EMPTY_STATE);

  useEffect(() => {
    if (!quill) return undefined;
    const settings = options.map((option) =>
      typeof option === 'string'
        ? { prefix: option, isAllowedInTitle: false }
        : option,
    );
    settingsRef.current = settings;

    quillToSettings.set(quill, [
      ...(quillToSettings.get(quill) || []),
      ...settings,
    ]);
    return () => {
      const allSettings = quillToSettings.get(quill);
      if (quillToSettings.has(quill)) {
        quillToSettings.set(quill, _.without(allSettings, ...settings));
      }
    };
  }, [quill, options]);

  useEffect(() => {
    if (!quill) return undefined;

    const reset = () => {
      quill.emitter.emit(EVENTS.RESET);
    };

    const listener = (prefixState: State | null) => {
      const isTarget =
        prefixState &&
        settingsRef.current.some(
          (setting) =>
            setting.prefix === prefixState.prefix &&
            (!prefixState.isInTitle || setting.isAllowedInTitle),
        );

      setState(isTarget ? { ...prefixState, reset } : EMPTY_STATE);
    };

    quill.on(EVENTS.CHANGE, listener);
    return () => {
      quill.off(EVENTS.CHANGE, listener);
    };
  }, [quill]);

  return state;
};

export default PrefixListener;
